import { Controller } from "@hotwired/stimulus"
import { scrollContainerFor } from "../utils/scroll_container"
import { activeTocLink, parseTocPage } from "../utils/toc_spy"

const TOC_SPY_LINE = 100

// Scroll-spy for the in-article Table of Contents card (purchases, File Hub
// surfaces that still show `.review-content-toc`). The Academy rail uses the
// same algorithm from academy-lesson; this controller exists because those
// pages do not mount academy-lesson.
export default class extends Controller {
  connect() {
    this.tocLinks = Array.from(this.element.querySelectorAll("a[href^='#']"))
    if (this.tocLinks.length === 0) return

    this.pinnedLink = null
    this.onScroll = () => this.scheduleActiveToc()
    window.addEventListener("scroll", this.onScroll, { passive: true, capture: true })
    this.onScrollGesture = () => { this.pinnedLink = null }
    window.addEventListener("wheel", this.onScrollGesture, { passive: true })
    window.addEventListener("touchmove", this.onScrollGesture, { passive: true })
    this.onRemotePage = () => this.scheduleActiveToc()
    this.pagerEl = this.element.closest("[data-controller~='article-pager']")
    this.pagerEl?.addEventListener("article-pager:page-changed", this.onRemotePage)
    this.updateActiveToc()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll, { capture: true })
    window.removeEventListener("wheel", this.onScrollGesture)
    window.removeEventListener("touchmove", this.onScrollGesture)
    this.pagerEl?.removeEventListener("article-pager:page-changed", this.onRemotePage)
    if (this.spyFrame) cancelAnimationFrame(this.spyFrame)
  }

  pinClickedLink(event) {
    const link = event.currentTarget
    if (!this.tocLinks.includes(link)) return

    this.pinnedLink = link
    this.markCurrentLink(link)
  }

  scheduleActiveToc() {
    if (this.spyFrame) return
    this.spyFrame = requestAnimationFrame(() => {
      this.spyFrame = null
      this.updateActiveToc()
    })
  }

  updateActiveToc() {
    if (!this.tocLinks?.length) return
    if (this.pinnedLink) return this.markCurrentLink(this.pinnedLink)

    const scroller = scrollContainerFor(this.element)
    const current = activeTocLink({
      entries: this.tocEntries(),
      spyLine: TOC_SPY_LINE,
      atBottom: this.scrollerAtBottom(scroller),
      currentPage: this.currentPage()
    })
    if (current) this.markCurrentLink(current)
  }

  tocEntries() {
    return this.tocLinks.map((link) => {
      const id = (link.getAttribute("href") || "").replace(/^#/, "")
      return {
        link,
        heading: id ? document.getElementById(id) : null,
        page: parseTocPage(link.dataset.tocPage)
      }
    })
  }

  currentPage() {
    const pager = this.element.closest("[data-controller~='article-pager']")
    return parseTocPage(pager?.dataset.articlePagerPage)
  }

  scrollerAtBottom(scroller) {
    return scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2
  }

  markCurrentLink(link) {
    this.tocLinks.forEach((other) => other.classList.remove("is-active"))
    link.classList.add("is-active")
  }
}
